import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { Grid, Button, Icon, makeStyles, Fab, Tabs, Tab } from '@material-ui/core';
import TokenImage from 'assets/images/token-24.svg';
import SettingsIcon from '@material-ui/icons/Settings';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import { useSelector } from 'react-redux';
import { RootStore } from 'redux/store';
import { SendTokensModal } from 'components/Modals/SendTokens';
import debounce from 'lodash.debounce';
import { useHistory } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import { withRouteInformation } from 'components/withRouteInformation';
import Tokens16 from 'assets/images/tokens-16.svg';
import './Banner.scss';

const useStyles = makeStyles({
	root: {
		overflow: 'visible',
	},
});

interface BannerProps {
	pathname: string;
	children: ReactNode;
}

const Banner: React.FC<BannerProps> = (props: BannerProps) => {
	const classes = useStyles();
	const history = useHistory();
	const currentTeam = useSelector((state: RootStore) => state.user.selectedTeam);
	const userState = useSelector((state: RootStore) => state.user);
	const [sendTokensModalOpen, setSendTokensModalOpen] = useState(false);
	const [showFabBtn, setShowFabBtn] = useState(false);
	const [tabValue, setTabValue] = useState(0);
	const [showTeamDetails, setShowTeamDetails] = useState(false);
	const [showSinglePost, setShowSinglePost] = useState(false);
	const { pathname } = props;
	const [currentUserTokensForTeam, setCurrentUserTokensForTeam] = useState(0);
	let tabsView: any = [];

	const TokenIcon = (
		<Icon
			classes={{
				root: classes.root,
			}}
		>
			<img alt='edit' src={TokenImage} className='tokens-icon' />
		</Icon>
	);

	const handleScroll = useCallback(() => {
		const pageScrolled = window.pageYOffset > 94;
		if (
			!showFabBtn &&
			pageScrolled &&
			!currentTeam?.isLocked &&
			(!currentTeam?.adminOnlyPosts || currentTeam?.isAdmin) &&
			!showTeamDetails &&
			!showSinglePost
		) {
			setShowFabBtn(true);
		} else if (showFabBtn && !pageScrolled) {
			setShowFabBtn(false);
		}
	}, [showFabBtn, currentTeam, showTeamDetails, showSinglePost]);

	const handleTabsClick = (event: React.ChangeEvent<{}>, newValue: number) => {
		setTabValue(newValue);
	};

	const handleTabClick = (tabClicked: string) => {
		history.push(`/team/${currentTeam?.teamId}/${tabClicked}`);
	};

	useEffect(() => {
		const debouncedHandleResize = debounce(() => {
			const bannerWidth = document.getElementById('banner-grid-item-id')?.clientWidth;
			const tabs = document.getElementById('banner-tabs');
			if (tabs) tabs.style.width = bannerWidth + 'px';
		}, 300);
		debouncedHandleResize();
		window.addEventListener('resize', debouncedHandleResize);

		return () => {
			window.removeEventListener('resize', debouncedHandleResize);
		};
	}, []);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll]);

	useEffect(() => {
		if (pathname.endsWith('/details/leaderboard')) {
			setShowTeamDetails(true);
			setShowSinglePost(false);
			setTabValue(0);
		} else if (pathname.endsWith('/all')) {
			setShowTeamDetails(false);
			setShowSinglePost(false);
			setTabValue(0);
		} else if (pathname.endsWith('/my')) {
			setShowTeamDetails(false);
			setShowSinglePost(false);
			setTabValue(1);
		} else if (pathname.endsWith('/details/settings')) {
			setShowTeamDetails(true);
			setShowSinglePost(false);
			setTabValue(2);
		} else if (pathname.endsWith('/details/badges')) {
			setShowTeamDetails(true);
			setShowSinglePost(false);
			setTabValue(3);
		} else if (pathname.endsWith('/details/users')) {
			setShowTeamDetails(true);
			setShowSinglePost(false);
			setTabValue(1);
		} else if (pathname.endsWith('/stats')) {
			setShowTeamDetails(false);
			setShowSinglePost(false);
			setTabValue(2);
		} else if (pathname.match('post')) {
			setShowSinglePost(true);
			setShowTeamDetails(false);
			setTabValue(0);
		}
	}, [pathname]);

	useEffect(() => {
		const currentUser = userState.users.find(user => user.userId === userState.userId);
		setCurrentUserTokensForTeam(currentUser ? currentUser?.tokens : 0);
	}, [userState]);

	if (showTeamDetails && !showSinglePost) {
		tabsView = [
			<Tab onClick={() => handleTabClick('details/leaderboard')} label='Leaderboard' title='Show team leaderboard' key='leaderboard' />,
			<Tab onClick={() => handleTabClick('details/users')} label='Members' title='View team Members' key='members' />,
			<Tab onClick={() => handleTabClick('details/settings')} label='Settings' title='View team Settings' key='settings' />,
			<Tab onClick={() => handleTabClick('details/badges')} id='badgesTab' label='Badges' title='View team Badges' key='badges' />,
		];
	} else if (showSinglePost && !showTeamDetails) {
		tabsView = [];
	} else {
		tabsView = [
			<Tab
				onClick={() => handleTabClick('all')}
				className='banner-tabs__all-tokens'
				label='All Tokens'
				title='Show all posts from this team'
				key='allTokens'
			/>,
			<Tab
				onClick={() => handleTabClick('my')}
				className='banner-tabs__my-tokens'
				label={
					<div className='my-tokens-wrapper'>
						My Tokens ({currentUserTokensForTeam} <img src={Tokens16} alt='token-icon' />)
					</div>
				}
				title='Show only posts from this team where I received tokens'
				key='myTokens'
			/>,
			<Tab onClick={() => handleTabClick('stats')} label='My Stats' title='Show my stats for this team' key='stats' />,
		];
	}
	return (
		<>
			<div className='banner-main'>
				<div className='banner-main__content-wrapper'>
					<Grid container>
						<Grid item id='banner-grid-item-id' className='banner-grid-item'>
							<div>
								<div className='banner-team-title' title={currentTeam?.teamName}>
									{currentTeam?.teamName}
								</div>
								<div>
									{!showTeamDetails && !showSinglePost ? (
										<Button
											onClick={() => handleTabClick('details/leaderboard')}
											className='team-details-btn'
											startIcon={<SettingsIcon className='team-details-icon' />}
										>
											Team Details
										</Button>
									) : (
										<Button
											onClick={() => handleTabClick('all')}
											className='team-details-btn'
											startIcon={<NavigateBeforeIcon className='team-details-icon' />}
										>
											Back
										</Button>
									)}
								</div>
							</div>

							{!currentTeam?.isLocked && (!currentTeam?.adminOnlyPosts || currentTeam?.isAdmin) && !showTeamDetails && (
								<Button
									variant='contained'
									color='default'
									className='send-tokens-button-wrapper'
									startIcon={TokenIcon}
									onClick={() => setSendTokensModalOpen(true)}
								>
									Send Tokens
								</Button>
							)}
						</Grid>
					</Grid>
				</div>
				<Tabs
					value={tabValue}
					onChange={handleTabsClick}
					aria-label='banner tabs'
					id='banner-tabs'
					variant='scrollable'
					scrollButtons='auto'
					TabIndicatorProps={{ style: { background: 'white' } }}
				>
					{tabsView}
				</Tabs>
				<SendTokensModal open={sendTokensModalOpen} onCloseClicked={() => setSendTokensModalOpen(false)} />
			</div>
			{showFabBtn && (
				<Fab color='secondary' aria-label='add' className='send-tokens-fab' onClick={() => setSendTokensModalOpen(true)}>
					<AddIcon />
				</Fab>
			)}
		</>
	);
};

export default withRouteInformation(Banner);
